// sim/resources.ts — food resource nodes: depletion + regeneration.
//
// A food node is an entity (KIND.Food) with an integer `foodAmount`. Ants
// consume 1 unit at a time (see consumeFood). When a node hits 0 it is removed
// from the grid bucket and a regen countdown starts; after `regenDelay` ticks it
// reappears on the SAME tile with `regenAmount`. This keeps the world persistent
// and sustainable.
//
// Food ids are tracked in a stable, compact registry (`foodIds`) so the regen
// pass iterates only food nodes in deterministic id order — never a full scan.
//
// Active/dirty sleeping (EFFICIENCY.md §B.6): a node that is ON-GRID has nothing
// to do in update() — its only per-tick work is the regen countdown, which only
// runs while it is OFF-GRID (depleted). So the moment a node becomes on-grid we
// mark it dormant (entities.sleep), and the moment it is emptied we wake it. The
// regen scan then skips dormant nodes. This is PROVABLY equivalent to the prior
// `onGrid === 1` skip — dormant is set EXACTLY when onGrid is 1 — so the state
// hash is byte-identical; it is a perf annotation only, never folded into a hash.

import { KIND } from '../types.ts';
import type { EntityId, System, Tick, ResourceConfig } from '../types.ts';
import type { EntitySystem } from '../engine/EntitySystem.ts';
import type { WorldGrid } from '../engine/WorldGrid.ts';

export class ResourceSystem implements System {
  readonly name = 'resources';

  private readonly entities: EntitySystem;
  private readonly grid: WorldGrid;
  private readonly config: ResourceConfig;
  /**
   * Optional ecosystem-event modifier (read-only here). When present, regen is
   * scaled by `foodRegenPct/100`; when absent it is neutral (100), so a world
   * without events is byte-identical. The event system owns the struct; we only
   * read it.
   */
  private readonly mods: { foodRegenPct: number } | undefined;

  /** Stable list of all food node ids (never recycled during a run). */
  private readonly foodIds: Int32Array;
  private foodCount = 0;
  /** Home tile for each food node, parallel to foodIds, so regen restores it. */
  private readonly homeX: Int32Array;
  private readonly homeY: Int32Array;
  /** Whether a food node is currently on the grid (1) or depleted (0). */
  private readonly onGrid: Uint8Array;
  /** entity id -> its slot in the food registry (-1 if not a food node). O(1) lookups. */
  private readonly foodSlot: Int32Array;

  /** Running total of food units consumed (debug / behavioural assertions). */
  consumedTotal = 0;

  constructor(
    entities: EntitySystem,
    grid: WorldGrid,
    config: ResourceConfig,
    maxFood: number,
    mods?: { foodRegenPct: number },
  ) {
    this.entities = entities;
    this.grid = grid;
    this.config = config;
    this.mods = mods;
    this.foodIds = new Int32Array(maxFood);
    this.homeX = new Int32Array(maxFood);
    this.homeY = new Int32Array(maxFood);
    this.onGrid = new Uint8Array(maxFood);
    this.foodSlot = new Int32Array(entities.capacity).fill(-1);
  }

  /**
   * Register a food node at (x,y) with `amount`. Spawns the entity, places it on
   * the grid, and records its home tile for regen. Call during world setup.
   * Returns the entity id.
   */
  addFood(x: number, y: number, amount: number): EntityId {
    const id = this.entities.spawn(KIND.Food, x, y);
    this.entities.foodAmount[id] = amount;
    this.entities.regenTimer[id] = 0;
    this.grid.add(id);
    // On-grid and full ⇒ inert: nothing for update() to do until it's consumed.
    this.entities.sleep(id);

    const slot = this.foodCount++;
    this.foodIds[slot] = id;
    this.homeX[slot] = x;
    this.homeY[slot] = y;
    this.onGrid[slot] = 1;
    this.foodSlot[id] = slot;
    return id;
  }

  /** Number of registered food nodes. */
  get count(): number {
    return this.foodCount;
  }

  /** Total food amount currently available on the grid (across all nodes). */
  totalRemaining(): number {
    let total = 0;
    for (let i = 0; i < this.foodCount; i++) {
      if (this.onGrid[i] === 1) total += this.entities.foodAmount[this.foodIds[i] as number] as number;
    }
    return total;
  }

  /**
   * Consume one unit from the food node `foodId` (assumed on-grid with amount>0).
   * Decrements the amount; if it reaches 0, removes it from the grid and starts
   * the regen countdown. Returns the energy yield (always 1 unit consumed here;
   * the caller converts units->energy). O(1) except the slot lookup, which is a
   * small bounded scan over the food registry.
   */
  consumeOne(foodId: EntityId): void {
    const amt = this.entities.foodAmount[foodId] as number;
    if (amt <= 0) return;
    this.entities.foodAmount[foodId] = amt - 1;
    this.consumedTotal++;
    if (amt - 1 === 0) {
      this.grid.remove(foodId);
      this.entities.regenTimer[foodId] = this.config.regenDelay;
      const slot = this.foodSlot[foodId] as number;
      if (slot >= 0) this.onGrid[slot] = 0;
      // Depleted: the regen countdown now needs ticking, so it must be processed.
      this.entities.wake(foodId);
    }
  }

  /**
   * Take up to `want` units from `foodId` in one go (a Worker loading cargo).
   * Returns the amount actually taken (may be less if the node is low). Starts
   * the regen countdown if the node is emptied. O(1) + the small slot lookup.
   */
  consume(foodId: EntityId, want: number): number {
    const amt = this.entities.foodAmount[foodId] as number;
    if (amt <= 0 || want <= 0) return 0;
    const taken = want < amt ? want : amt;
    this.entities.foodAmount[foodId] = amt - taken;
    this.consumedTotal += taken;
    if (amt - taken === 0) {
      this.grid.remove(foodId);
      this.entities.regenTimer[foodId] = this.config.regenDelay;
      const slot = this.foodSlot[foodId] as number;
      if (slot >= 0) this.onGrid[slot] = 0;
      // Depleted: the regen countdown now needs ticking, so it must be processed.
      this.entities.wake(foodId);
    }
    return taken;
  }

  /**
   * Tick the regen countdown for every depleted node; re-add to the grid with
   * `regenAmount` when the timer expires. Deterministic id-order iteration.
   */
  update(_tick: Tick): void {
    // Event-scaled regen: foodRegenPct/100 (neutral 100 ⇒ unchanged amount).
    const scale = this.mods ? this.mods.foodRegenPct : 100;
    const eff = Math.floor((this.config.regenAmount * scale) / 100);
    for (let i = 0; i < this.foodCount; i++) {
      const id = this.foodIds[i] as number;
      // Dormant ⇒ on-grid and inert: skip. This is the active/dirty optimization;
      // it subsumes the `onGrid === 1` skip (dormant is set iff onGrid is 1), so
      // the behaviour — and the state hash — is unchanged.
      if (this.entities.isDormant(id)) continue;
      if (this.onGrid[i] === 1) continue;
      const timer = this.entities.regenTimer[id] as number;
      if (timer > 1) {
        this.entities.regenTimer[id] = timer - 1;
      } else {
        // Timer hits 0 this tick: restore the node at its home tile.
        this.entities.regenTimer[id] = 0;
        this.entities.foodAmount[id] = eff;
        this.entities.posX[id] = this.homeX[i] as number;
        this.entities.posY[id] = this.homeY[i] as number;
        this.grid.add(id);
        this.onGrid[i] = 1;
        // Back on-grid and full ⇒ inert again until next consumption.
        this.entities.sleep(id);
      }
    }
  }

  /**
   * FoodSurplusBloom onset: add `extra` to every ON-GRID food node, in
   * deterministic registry order. Allocation-free; called only by the event
   * system. Depleted (off-grid) nodes are left to their normal regen path.
   */
  bloom(extra: number): void {
    if (extra <= 0) return;
    for (let i = 0; i < this.foodCount; i++) {
      if (this.onGrid[i] !== 1) continue;
      const id = this.foodIds[i] as number;
      this.entities.foodAmount[id] = (this.entities.foodAmount[id] as number) + extra;
    }
  }
}
